import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, CheckCircle, Lock, CreditCard } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";

const API = "/api";
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function CheckoutOverlay({ open, onClose, cart, onPaymentComplete }) {
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = cart.reduce((s, i) => s + i.quantity, 0);

  const handlePaymentSuccess = async (details) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/payments/record`, {
        order_id: details.id,
        payer_email: details.payer?.email_address || null,
        amount: total,
        currency: "MXN",
        items: cart.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        status: details.status,
        shipping: details.purchase_units?.[0]?.shipping || null, // <-- NUEVO
      });
    } catch (e) {
      console.error("Error recording payment:", e);
    }
    setProcessing(false);
    setPaymentSuccess(true);
    setTimeout(() => {
      setPaymentSuccess(false);
      if (onPaymentComplete) onPaymentComplete();
    }, 4000);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          data-testid="checkout-overlay"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={!paymentSuccess && !processing ? onClose : undefined}
          />

          {/* Checkout card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative z-10 w-full max-w-lg mx-4 bg-[#1A1A1A] border border-white/10 overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <div className="flex items-center gap-3">
                <Lock size={16} className="text-[#D4AF37]" />
                <h2
                  className="text-xl font-bold text-[#F5F5F0]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Checkout Seguro
                </h2>
              </div>
              {!paymentSuccess && !processing && (
                <button
                  onClick={onClose}
                  className="text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors p-1"
                  data-testid="close-checkout"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {paymentSuccess ? (
              /* Success state */
              <div className="px-6 py-16 flex flex-col items-center text-center gap-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <CheckCircle size={72} className="text-[#4A7C59]" />
                </motion.div>
                <h3
                  className="text-2xl font-bold text-[#F5F5F0]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid="payment-success-title"
                >
                  ¡Pago exitoso!
                </h3>
                <p className="text-sm text-[#A3A3A3] max-w-xs">
                  Gracias por tu compra. Recibirás un correo de confirmación de PayPal.
                </p>
              </div>
            ) : (
              <>
                {/* Order summary */}
                <div className="px-6 py-5 border-b border-white/5">
                  <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-4">Resumen del pedido</p>
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.id} className="flex items-center justify-between" data-testid={`checkout-item-${item.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#262626] overflow-hidden flex-shrink-0">
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          </div>
                          <div>
                            <p className="text-sm text-[#F5F5F0]">{item.name}</p>
                            <p className="text-xs text-[#A3A3A3]">x{item.quantity}</p>
                          </div>
                        </div>
                        <span className="text-sm text-[#F5F5F0]">
                          ${(item.price * item.quantity).toFixed(0)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
                    <span className="text-sm text-[#A3A3A3] uppercase tracking-wider">Total</span>
                    <span
                      className="text-2xl font-bold text-[#D4AF37]"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                      data-testid="checkout-total"
                    >
                      ${total.toFixed(0)} <span className="text-sm font-normal text-[#A3A3A3]">MXN</span>
                    </span>
                  </div>
                </div>

                {/* PayPal buttons */}
                <div className="px-6 py-6">
                  <div className="flex items-center gap-2 mb-4 justify-center">
                    <CreditCard size={14} className="text-[#A3A3A3]" />
                    <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">
                      Selecciona tu método de pago
                    </p>
                  </div>

                  <div className="bg-[#f5f5f5] rounded-md p-4" data-testid="paypal-container">
                    <PayPalScriptProvider
                      options={{
                        clientId: PAYPAL_CLIENT_ID,
                        currency: "MXN",
                        intent: "capture",
                        components: "buttons",
                      }}
                    >
                      <PayPalButtons
                        style={{
                          layout: "vertical",
                          color: "gold",
                          shape: "rect",
                          label: "pay",
                          height: 50,
                          tagline: false,
                        }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            application_context: {
                              shipping_preference: "GET_FROM_FILE", // <-- NUEVO: pide dirección al comprador
                            },
                            purchase_units: [
                              {
                                amount: {
                                  currency_code: "MXN",
                                  value: total.toFixed(2),
                                  breakdown: {
                                    item_total: {
                                      currency_code: "MXN",
                                      value: total.toFixed(2),
                                    },
                                  },
                                },
                                items: cart.map((item) => ({
                                  name: item.name,
                                  unit_amount: {
                                    currency_code: "MXN",
                                    value: item.price.toFixed(2),
                                  },
                                  quantity: String(item.quantity),
                                })),
                                description: "Pura Madre - Salsas Artesanales",
                              },
                            ],
                          });
                        }}
                        onApprove={async (data, actions) => {
                          const details = await actions.order.capture();
                          handlePaymentSuccess(details);
                        }}
                        onError={(err) => {
                          console.error("PayPal error:", err);
                        }}
                      />
                    </PayPalScriptProvider>
                  </div>

                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Lock size={12} className="text-[#A3A3A3]" />
                    <p className="text-[10px] text-[#A3A3A3]">
                      Pago seguro procesado por PayPal. Acepta tarjeta de crédito, débito y cuenta PayPal.
                    </p>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
